import React from 'react';
import _ from 'underscore';

import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { RouterError } from '../../../../bootstrap/routerHelpers/RouterError';
import { SessionManagementService } from '../../../../sessionManagement/SessionManagementService';
import { Sites } from '../../../../siteManagement/services/resources/Sites';
import { IGetSiteSettingsForSiteAdminResult } from '../../../ServerApi/requests/siteSettings/GetSiteSettingsForSiteAdminRequest';
import { ServerSettings } from '../../../services/ServerSettings';
import { SiteAuthenticationSettings, SiteAuthenticationSettingsProps } from '../../../siteAuthSettings/components/SiteAuthenticationSettings';
import { ISiteAuthenticationSettings, SiteAuthenticationSettingsService } from '../../../siteAuthSettings/services/SiteAuthenticationSettingsService';
import { ISiteSettingsForSiteAdmin } from '../../../types/ISiteSettingsForSiteAdmin';


export interface AuthenticationSiteSettingsSubspaceOwnProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly server: SessionManagementService;
  readonly isSessionServerAdmin: boolean;
}

export interface AuthenticationSiteSettingsSubspaceServicesProps {
  readonly serverSettingsService: ServerSettings;
  readonly sites: Sites;
}

export type AuthenticationSiteSettingsSubspaceProps = AuthenticationSiteSettingsSubspaceOwnProps & AuthenticationSiteSettingsSubspaceServicesProps;

export interface AuthenticationSiteSettingsSubspaceState {
  readonly hasDataToRender: boolean;
  readonly hasError: boolean;
  readonly siteAuthenticationSettings: ISiteAuthenticationSettings;
  readonly siteSettings: IGetSiteSettingsForSiteAdminResult;
  readonly siteAuthenticationSettingsProps: SiteAuthenticationSettingsProps;
}

export class AuthenticationSiteSettingsSubspace extends React.PureComponent<AuthenticationSiteSettingsSubspaceProps, AuthenticationSiteSettingsSubspaceState> {

  private unmounted: boolean = false;

  private initialState: AuthenticationSiteSettingsSubspaceState = {hasDataToRender: false, hasError: false, siteSettings: undefined, siteAuthenticationSettings: undefined, siteAuthenticationSettingsProps: undefined};

  public constructor(props: AuthenticationSiteSettingsSubspaceProps) {
    super(props);
    this.state = this.initialState;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate(prevProps: AuthenticationSiteSettingsSubspaceProps) {
    if (!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.updateState(this.initialState);
      this.getData();
      return;
    }

    if (!_.isUndefined(this.state.siteSettings) && !_.isUndefined(this.state.siteAuthenticationSettings)) {
        this.updateState({hasDataToRender: true});
    }
  }

  public render() {
    if (!this.state.hasDataToRender) {
      return null;
    } else if (this.state.hasError) {
      return <RouterError errorType={'default'} urls={this.props.urls} />;
    }

    return (
      <SiteAuthenticationSettings {...this.state.siteAuthenticationSettingsProps}/>
    );
  }

  private updateState<K extends keyof AuthenticationSiteSettingsSubspaceState>(newState: Pick<AuthenticationSiteSettingsSubspaceState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getData() {
    Promise.all([this.getSiteSettings(), this.getSiteAuthenticationSettings()]).then(() => this.setSiteAuthenticationSettingsProps());
  }

  private getSiteSettings = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const siteSettings = await this.props.sites.getCurrentSiteSettingsForSiteAdmin();
      this.updateState({siteSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  };

  private getSiteAuthenticationSettings = async () => {
    try {
      const siteAuthSettings = await SiteAuthenticationSettingsService.getSiteAuthenticationSettings(/* refresh */ true);
      this.updateState({siteAuthenticationSettings: siteAuthSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  };

  private setSiteAuthenticationSettingsProps() {
    const props = {
      showEncryptionStatus: true,
      server: this.props.server,
      siteSettingsForSiteAdmin: this.state.siteSettings as unknown as ISiteSettingsForSiteAdmin
    };
    this.updateState({siteAuthenticationSettingsProps: props});
  }
}

